import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import PhoneInput, { isPossiblePhoneNumber } from "react-phone-number-input";
import { faMinus, faPlus } from "@fortawesome/free-solid-svg-icons";
import ReactDatePicker from "react-datepicker";
import Input from "../../../nodes/Logic/Inputs/Input";
import MapInput from "../../../nodes/Logic/Inputs/MapInput";
import {
  loadAssetOptionsPaginated,
  loadContactOptionsPaginated,
  loadEventOptionsPaginated,
} from "../../../../proxyAPI";
import { contactOption } from "../../../../Reporting/RealtimeReportingPage";
import { pageProps } from "../../../../helpers/pageProps";
import timezones from "../../../nodes/Logic/timezones";
import TokenEditor from "../../TokenEditor";
import { InputArgument } from "./types";
import { formatDateValue } from "./dateUtils";
import { getFieldError } from "./fieldValidation";
import { getButtonInputOptions } from "../../inputArgumentOptionHelpers";

interface InputArgumentFieldProps {
  arg: InputArgument;
  /** The current values for this argument (i.e. formData[arg.Field]) */
  values: string[];
  /** Called with the complete new values array whenever the field changes */
  onChange: (newValues: string[]) => void;
  /** Pre-filtered list of input arguments shown inside the token editor */
  inputArgumentsInEditor: InputArgument[];
  /** Render contact/group selectors as offcanvas when true */
  useOffcanvasForContactGroupModal?: boolean;
}

const VariableTextInput = ({
  arg,
  values,
  onChange,
  placeholder,
}: {
  arg: InputArgument;
  values: string[];
  onChange: (newValues: string[]) => void;
  placeholder: string;
}) => (
  <>
    {arg.IsMultiple && (
      <button
        type="button"
        title="Add input"
        className="btn btn-outline-primary btn-sm border-0 float-end"
        onClick={() => onChange([...(values ?? []), ""])}
      >
        <FontAwesomeIcon icon={faPlus} /> Add
      </button>
    )}
    {(values.length > 0 ? values : [""]).map((value, index) => (
      <div key={index} className="d-flex mb-1">
        <div className="flex-grow-1">
          <textarea
            placeholder={placeholder}
            className="form-control mt-1"
            value={value}
            onChange={(e) => {
              if (!values || values.length === 0) {
                onChange([e.target.value]);
                return;
              }
              onChange(
                values.map((item, i) => (i === index ? e.target.value : item))
              );
            }}
          />
        </div>
        {arg.IsMultiple && values.length > 1 && (
          <button
            type="button"
            title="Remove input"
            className="btn btn-outline-danger border-0 float-end"
            onClick={() => onChange(values.filter((_, i) => i !== index))}
          >
            <FontAwesomeIcon icon={faMinus} />
          </button>
        )}
      </div>
    ))}
  </>
);

const VariableDurationInput = ({
  arg,
  values,
  onChange,
  placeholder,
}: {
  arg: InputArgument;
  values: string[];
  onChange: (newValues: string[]) => void;
  placeholder: string;
}) => (
  <>
    {arg.IsMultiple && (
      <button
        type="button"
        title="Add input"
        className="btn btn-outline-primary btn-sm border-0 float-end"
        onClick={() => onChange([...(values ?? []), "0|second"])}
      >
        <FontAwesomeIcon icon={faPlus} /> Add
      </button>
    )}
    {(values.length > 0 ? values : [""]).map((value, index) => (
      <div key={index} className="d-flex mb-1">
        <div className="flex-grow-1">
          <Input
            placeholder={placeholder}
            inputHelp=""
            value={[value.split("|")[0] || ""]}
            onChange={([v]) =>
              onChange(
                values.map((item, i) =>
                  i === index ? v + "|" + (item.split("|")[1] || "") : item
                )
              )
            }
          />
        </div>
        <div className="flex-grow-1">
          <Input
            placeholder={placeholder}
            inputType="select"
            options={
              arg.Unit?.map((unit) => ({ value: unit, label: unit })) || []
            }
            isValidNewOption={() => false}
            inputHelp=""
            value={[value.split("|")[1] || ""]}
            onChange={([v]) =>
              onChange(
                values.map((item, i) =>
                  i === index ? (item.split("|")[0] || "") + "|" + v : item
                )
              )
            }
          />
        </div>
        {arg.IsMultiple && values.length > 1 && (
          <button
            type="button"
            title="Remove input"
            className="btn btn-outline-danger border-0 float-end"
            onClick={() => onChange(values.filter((_, i) => i !== index))}
          >
            <FontAwesomeIcon icon={faMinus} />
          </button>
        )}
      </div>
    ))}
  </>
);

const parseEditorValue = (value: string) => {
  if (!value) {
    return {};
  }

  try {
    return JSON.parse(value);
  } catch {
    return {};
  }
};

const getRawEditorState = (editorValue: any) => {
  return editorValue?.EditorState ??
    (editorValue?.blocks ? editorValue : undefined);
};

const isEmptyRawEditorState = (rawEditorState: any) => {
  return (
    rawEditorState?.blocks?.length === 1 &&
    rawEditorState.blocks[0]?.text === "" &&
    Object.keys(rawEditorState?.entityMap ?? {}).length === 0
  );
};

export default function InputArgumentField({
  arg,
  values,
  onChange,
  inputArgumentsInEditor,
  useOffcanvasForContactGroupModal = false,
}: InputArgumentFieldProps) {
  const placeholder = arg.Default ? `Default: ${arg.Default}` : "Enter a value";

  return (
    <div>
      {["long-text", "short-text"].includes(arg.Format) &&
        !arg.IsSelect &&
        (arg?.DataStructure === "Duration" ? (
          <VariableDurationInput
            arg={arg}
            values={values}
            onChange={onChange}
            placeholder={placeholder}
          />
        ) : (
          <VariableTextInput
            arg={arg}
            values={values}
            onChange={onChange}
            placeholder={placeholder}
          />
        ))}

      {["editor"].includes(arg.Format) && (
        <>
          {(values.length > 0 ? values : [""]).map((value, index) => {
            const editorValue = parseEditorValue(value);
            const rawEditorState = getRawEditorState(editorValue);

            return (
              <div
                key={`${index}-${values.length > 0 ? "initialized" : "empty"}`}
                className="mb-1"
                data-token-editor
              >
                {React.createElement(TokenEditor as any, {
                  style: { height: 300 },
                  showButtons: true,
                  initialText: "",
                  rawEditorState,
                  showShortlinksButton: false,
                  tokenTags: [
                    "contact_field",
                    "input_argument",
                    "input_argument_count",
                    "workflow_name",
                    "guid",
                    "component_name",
                    "current_time",
                    "customer_name",
                    "survey",
                  ],
                  inputArguments: inputArgumentsInEditor,
                  onChange: ({
                    rawEditorState,
                    tokens,
                  }: {
                    rawEditorState: unknown;
                    tokens: unknown;
                  }) => {
                    if (
                      (!values || values.length === 0) &&
                      isEmptyRawEditorState(rawEditorState)
                    ) {
                      return;
                    }

                    if (!values || values.length === 0) {
                      onChange([
                        JSON.stringify({
                          EditorState: rawEditorState,
                          Tokens: tokens,
                        }),
                      ]);
                      return;
                    }
                    onChange(
                      values.map((item, i) =>
                        i === index
                          ? JSON.stringify({
                              EditorState: rawEditorState,
                              Tokens: tokens,
                            })
                          : item
                      )
                    );
                  },
                })}
              </div>
            );
          })}
        </>
      )}

      {["short-text"].includes(arg.Format) &&
        arg.IsSelect &&
        (arg?.DataStructure === "" ? (
          <Input
            placeholder={placeholder}
            inputType={arg.IsSelect ? "select" : undefined}
            isMulti={arg.IsMultiple}
            options={
              arg.IsSelect
                ? arg.SelectOptions?.map((opt) => ({
                    value: opt,
                    label: opt,
                  })) || []
                : undefined
            }
            isValidNewOption={(v: string) => v.trim() !== ""}
            inputHelp=""
            value={values}
            onChange={onChange}
          />
        ) : arg?.DataStructure === "Buttons" ? (
          <Input
            placeholder={placeholder}
            inputType="button"
            isMultiple={arg.IsMultiple}
            options={getButtonInputOptions(arg.SelectOptions, arg.SelectOptionsIcons)}
            isValidNewOption={() => false}
            inputHelp=""
            value={values}
            onChange={onChange}
          />
        ) : null)}

      {["configuration"].includes(arg.Format) &&
        (arg?.DataStructure === "Time Zones" ? (
          <Input
            placeholder={placeholder}
            inputType="select"
            isMulti={arg.IsMultiple}
            options={[
              {
                value: "",
                label: `Project default (${(pageProps as any).defaultTZ})`,
              },
              ...timezones.map((tz: any) => ({
                value: tz.tzCode,
                label: tz.label,
              })),
            ]}
            isValidNewOption={() => false}
            inputHelp=""
            value={values}
            onChange={onChange}
          />
        ) : arg?.DataStructure === "Yes / No" ? (
          <Input
            placeholder={placeholder}
            inputType="button"
            isMulti={arg.IsMultiple}
            options={[
              {
                value: "no",
                label: "No",
              },
              {
                value: "yes",
                label: "Yes",
              },
            ]}
            isValidNewOption={() => false}
            inputHelp=""
            value={values}
            onChange={onChange}
          />
        ) : arg?.DataStructure === "Buttons" ? (
          <Input
            placeholder={placeholder}
            inputType="button"
            isMultiple={arg.IsMultiple}
            options={getButtonInputOptions(arg.SelectOptions, arg.SelectOptionsIcons)}
            isValidNewOption={() => false}
            inputHelp=""
            value={values}
            onChange={onChange}
          />
        ) : arg?.DataStructure === "Duration" ? (
          <VariableDurationInput
            arg={arg}
            values={values}
            onChange={onChange}
            placeholder={placeholder}
          />
        ) : (pageProps as any).configurations?.[arg?.DataStructure ?? ""] ? (
          <Input
            placeholder={placeholder}
            inputType="select"
            isMulti={arg.IsMultiple}
            options={Object.entries(
              (pageProps as any).configurations?.[arg?.DataStructure ?? ""] ||
                {}
            ).map(([value, label]) => ({ value, label }))}
            isValidNewOption={() => false}
            inputHelp=""
            value={values}
            onChange={onChange}
          />
        ) : (
          <VariableTextInput
            arg={arg}
            values={values}
            onChange={onChange}
            placeholder={placeholder}
          />
        ))}

      {["geo-json"].includes(arg.Format) && (
        <Input
          inputType="map"
          mapDrawingModes={arg.GeoJsonTypes}
          value={values}
          onChange={onChange}
          isMultiple={arg.IsMultiple}
        />
      )}

      {["json"].includes(arg.Format) && (
        <>
          {arg.IsMultiple && (
            <button
              type="button"
              title="Add input"
              className="btn btn-outline-primary btn-sm border-0 float-end"
              onClick={() => onChange([...(values ?? []), ""])}
            >
              <FontAwesomeIcon icon={faPlus} /> Add
            </button>
          )}
          {(values.length > 0 ? values : [""]).map((value, index) => (
            <div key={index} className="d-flex mb-1">
              <div className="flex-grow-1">
                <Input
                  inputType="textArea"
                  placeholder={placeholder}
                  value={[value]}
                  onChange={([v]) => {
                    const jsonString = v;
                    onChange(
                      values.map((item, i) => (i === index ? jsonString : item))
                    );
                  }}
                />
              </div>
              {arg.IsMultiple && values.length > 1 && (
                <button
                  type="button"
                  title="Remove input"
                  className="btn btn-outline-danger border-0 float-end"
                  onClick={() => onChange(values.filter((_, i) => i !== index))}
                >
                  <FontAwesomeIcon icon={faMinus} />
                </button>
              )}
            </div>
          ))}
        </>
      )}

      {["email"].includes(arg.Format) && (
        <Input
          placeholder={placeholder}
          isMulti={arg.IsMultiple}
          inputHelp=""
          value={values}
          isValidNewOption={(emailString: string) => {
            const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            return re.test(emailString);
          }}
          onChange={onChange}
        />
      )}

      {["number"].includes(arg.Format) && (
        <Input
          placeholder={placeholder}
          isMulti={arg.IsMultiple}
          inputType={arg.IsSelect ? "select" : undefined}
          options={
            arg.IsSelect
              ? arg.SelectOptions?.map((opt) => ({
                  value: opt,
                  label: opt,
                })) || []
              : undefined
          }
          isValidNewOption={(v: string) => !!parseFloat(v.trim())}
          onCreateOption={(v: string) => {
            if (!!parseFloat(v.trim())) {
              onChange([...(values || []), v.trim()]);
            }
          }}
          inputHelp=""
          value={values}
          onChange={onChange}
          type="number"
          min={arg.Min}
          max={arg.Max}
        />
      )}

      {(arg.Format === "date" ||
        arg.Format === "time" ||
        arg.Format === "dateTime") && (
        <>
          {arg.IsMultiple && (
            <button
              type="button"
              title="Add input"
              className="btn btn-outline-primary btn-sm border-0 float-end"
              onClick={() => {
                const date = arg.Default
                  ? new Date(String(arg.Default))
                  : new Date();
                onChange([
                  ...(values ?? []),
                  formatDateValue(date, arg.Format),
                ]);
              }}
            >
              <FontAwesomeIcon icon={faPlus} /> Add
            </button>
          )}
          {(values.length > 0 ? values : [""]).map((value, index) => (
            <div key={index} className="d-flex mb-1">
              <div className="flex-grow-1">
                <ReactDatePicker
                  showTimeSelect={
                    arg.Format === "time" || arg.Format === "dateTime"
                  }
                  showTimeSelectOnly={arg.Format === "time"}
                  className="form-control"
                  selected={
                    value &&
                    (arg.Format === "time"
                      ? new Date(
                          new Date().setHours(
                            ...(value.split(":").map(Number) as [
                              number,
                              number
                            ])
                          )
                        )
                      : new Date(value))
                  }
                  dateFormat={
                    (arg.Format === "dateTime" && "yyyy-MM-dd HH:mm") ||
                    (arg.Format === "date" && "yyyy-MM-dd") ||
                    (arg.Format === "time" && "HH:mm") ||
                    undefined
                  }
                  timeFormat="HH:mm"
                  onChange={(date: Date | null) => {
                    if (!date) {
                      onChange([""]);
                      return;
                    }
                    if (!values || values.length === 0) {
                      onChange([formatDateValue(date, arg.Format)]);
                      return;
                    }

                    onChange(
                      values.map((item, i) =>
                        i === index ? formatDateValue(date, arg.Format) : item
                      )
                    );
                  }}
                />
              </div>
              {arg.IsMultiple && values.length > 1 && (
                <button
                  type="button"
                  title="Remove input"
                  className="btn btn-outline-danger border-0 float-end"
                  onClick={() => onChange(values.filter((_, i) => i !== index))}
                >
                  <FontAwesomeIcon icon={faMinus} />
                </button>
              )}
            </div>
          ))}
        </>
      )}

      {["sms", "voice"].includes(arg.Format) && (
        <>
          {arg.IsMultiple && (
            <button
              type="button"
              title="Add input"
              className="btn btn-outline-primary btn-sm border-0 float-end"
              onClick={() => onChange([...(values ?? []), ""])}
            >
              <FontAwesomeIcon icon={faPlus} /> Add
            </button>
          )}
          {(values.length > 0 ? values : [""]).map((value, index) => (
            <div key={index} className="d-flex mb-1">
              <div className="flex-grow-1">
                <PhoneInput
                  placeholder={placeholder}
                  countrySelectProps={{ className: `form-select` }}
                  numberInputProps={{
                    className: `form-control ${
                      value && isPossiblePhoneNumber(value)
                        ? ""
                        : "border-danger"
                    }`,
                    title:
                      value && isPossiblePhoneNumber(value)
                        ? "Number is valid"
                        : "Number is not valid",
                  }}
                  defaultCountry={
                    (pageProps as any).defaultCountry?.country_code || "GB"
                  }
                  value={value}
                  onChange={(val: string | undefined) =>
                    onChange(
                      values.map((item, i) => (i === index ? val ?? "" : item))
                    )
                  }
                />
              </div>
              {arg.IsMultiple && values.length > 1 && (
                <button
                  type="button"
                  title="Remove input"
                  className="btn btn-outline-danger border-0 float-end"
                  onClick={() => onChange(values.filter((_, i) => i !== index))}
                >
                  <FontAwesomeIcon icon={faMinus} />
                </button>
              )}
            </div>
          ))}
        </>
      )}

      {["asset"].includes(arg.Format) && (
        <Input
          placeholder="Select assets"
          value={values}
          onChange={onChange}
          loadOptions={loadAssetOptionsPaginated}
          options={undefined}
          inputType="select"
          isMulti={arg.IsMultiple}
          isClearable={!arg.IsRequiredManual}
        />
      )}

      {["contact"].includes(arg.Format) && (
        <Input
          placeholder="Select contacts"
          value={values}
          onChange={onChange}
          inputType="contact"
          isMultiple={arg.IsMultiple}
          modalAsOffcanvas={useOffcanvasForContactGroupModal}
        />
      )}

      {["group"].includes(arg.Format) && (
        <Input
          placeholder="Select groups"
          value={values}
          onChange={onChange}
          inputType="contactSet"
          isMultiple={arg.IsMultiple}
          modalAsOffcanvas={useOffcanvasForContactGroupModal}
        />
      )}

      {["event"].includes(arg.Format) && (
        <Input
          placeholder="Select events"
          value={values}
          onChange={onChange}
          loadOptions={(search: string, prevOptions: unknown) =>
            loadEventOptionsPaginated(search, prevOptions)
          }
          inputType="select"
          isMulti={arg.IsMultiple}
          isClearable={!arg.IsRequiredManual}
        />
      )}

      {["ticket"].includes(arg.Format) && (
        <Input
          placeholder="Enter ticket reference"
          value={values}
          onChange={onChange}
          isMulti={arg.IsMultiple}
          isClearable={!arg.IsRequiredManual}
          isValidNewOption={(v: string) => v.trim() !== ""}
          inputHelp=""
        />
      )}

      {["binary"].includes(arg.Format) && (
        <input
          type="file"
          className="form-control"
          placeholder="Select files"
          multiple={arg.IsMultiple}
          onChange={async (e) => {
            if (!e.target.files?.[0]) {
              onChange([]);
              return;
            }
            try {
              const encoded = await Promise.all(
                Array.from(e.target.files).map(
                  (f) =>
                    new Promise<string>((resolve, reject) => {
                      const reader = new FileReader();
                      reader.readAsDataURL(f);
                      reader.onerror = reject;
                      reader.onload = () =>
                        resolve(
                          `filename:${f.name}|${(
                            reader.result as string
                          ).replace(";base64,", "|base64:")}`
                        );
                    })
                )
              );
              onChange(encoded);
            } catch (err) {
              console.error(err);
            }
          }}
        />
      )}

      {getFieldError(arg, values ?? []) && (
        <div className="mt-1">{getFieldError(arg, values ?? [])}</div>
      )}
    </div>
  );
}
